import type {
  CreateProjectDto,
  ProjectListItem,
  ProjectOverviewModel,
  SortField,
  SortOrder,
  UIDResponse,
  UpdateProjectDto,
} from '@/types/project.types';
import { apiClient } from './client';

/** Server-side list filter (matches backend GET /projects query params). */
export interface ProjectsListFilterParams {
  sort?: SortField;
  order?: SortOrder;
  // biome-ignore lint/style/useNamingConvention: Backend API uses snake_case
  owner_id?: string | null;
}

export interface ProjectsListParams extends ProjectsListFilterParams {
  page?: number;
  size?: number;
}

const DEFAULT_PAGE_SIZE = 8;

function appendListFilterParams(searchParams: URLSearchParams, params?: ProjectsListFilterParams | null): void {
  if (!params) return;
  if (params.sort != null) searchParams.set('sort', params.sort);
  if (params.order != null) searchParams.set('order', params.order);
  if (params.owner_id != null && params.owner_id !== '') searchParams.set('owner_id', params.owner_id);
}

export const projectsApi = {
  // GET /projects?page=&size=&sort=&order=&owner_id= - one page of projects, server-side ordered/filtered.
  getAll: async (params?: ProjectsListParams): Promise<ProjectListItem[]> => {
    const searchParams = new URLSearchParams();
    appendListFilterParams(searchParams, params);
    searchParams.set('page', String(params?.page ?? 1));
    searchParams.set('size', String(params?.size ?? DEFAULT_PAGE_SIZE));
    const { data } = await apiClient.get<ProjectListItem[]>(`/projects?${searchParams.toString()}`);
    return data;
  },

  // GET /projects-main-page?page=&size=&sort=&order=&owner_id= - performance-optimised variant for the main page list.
  getAllMainPage: async (params?: ProjectsListParams): Promise<ProjectListItem[]> => {
    const searchParams = new URLSearchParams();
    appendListFilterParams(searchParams, params);
    searchParams.set('page', String(params?.page ?? 1));
    searchParams.set('size', String(params?.size ?? DEFAULT_PAGE_SIZE));
    const { data } = await apiClient.get<ProjectListItem[]>(`/projects-main-page?${searchParams.toString()}`);
    return data;
  },

  // GET /projects/count?owner_id= - total project count with same owner filter.
  getCount: async (params?: Pick<ProjectsListFilterParams, 'owner_id'> | null): Promise<number> => {
    const searchParams = new URLSearchParams();
    if (params?.owner_id != null && params.owner_id !== '') searchParams.set('owner_id', params.owner_id);
    const query = searchParams.toString();
    const url = query ? `/projects/count?${query}` : '/projects/count';
    const { data } = await apiClient.get<{ total: number }>(url);
    return data.total;
  },

  // GET /project/{id}/overview - aggregated project data for the detail page
  // (core fields + model preview URLs/IDs + detections) in a single round-trip.
  getOverview: async (projectId: string): Promise<ProjectOverviewModel> => {
    const { data } = await apiClient.get<ProjectOverviewModel>(`/project/${projectId}/overview`);
    return data;
  },

  // POST /project
  create: async (project: CreateProjectDto): Promise<UIDResponse> => {
    const { data } = await apiClient.post<UIDResponse>('/project', project);
    return data;
  },

  // PUT /project/{id}
  update: async (projectId: string, project: UpdateProjectDto): Promise<void> => {
    await apiClient.put(`/project/${projectId}`, project);
  },

  // DELETE /project/{id}
  delete: async (projectId: string): Promise<void> => {
    await apiClient.delete(`/project/${projectId}`);
  },

  // POST /projects/bulk-delete
  bulkDelete: async (
    projectIds: string[]
  ): Promise<{
    deleted: string[];
    failed: Array<{
      // biome-ignore lint/style/useNamingConvention: Backend API uses snake_case
      project_id: string;
      success: boolean;
      error: string;
    }>;
  }> => {
    const { data } = await apiClient.post<{
      deleted: string[];
      failed: Array<{
        // biome-ignore lint/style/useNamingConvention: Backend API uses snake_case
        project_id: string;
        success: boolean;
        error: string;
      }>;
    }>('/projects/bulk-delete', {
      // biome-ignore lint/style/useNamingConvention: Backend API uses snake_case
      project_ids: projectIds,
    });
    return data;
  },

  // GET /3d/embedded/{project_id}/download - download original embedded STP file
  downloadEmbeddedSTP: async (projectId: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(`/3d/embedded/${projectId}/download`, {
      responseType: 'blob',
    });
    return data;
  },

  // GET /nc-document/{project_id}/download - download NC document as XLSM
  downloadNCDocument: async (projectId: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(`/nc-document/${projectId}/download`, {
      responseType: 'blob',
    });
    return data;
  },

  // POST /nc-document/{project_id}/regenerate - regenerate NC document (returns 200 OK)
  regenerateNCDocument: async (projectId: string): Promise<void> => {
    await apiClient.post(`/nc-document/${projectId}/regenerate`);
  },
};
